import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import CustomCkeditor from "../../../common/customeditor";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import * as agreementService from "../../../services/agreements.service";
import { Tooltip, Whisper } from "rsuite";

const AddAgreementTemplate = () => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [placeholders, setPlaceholders] = useState([]);
    const [formData, setFormData] = useState({
        title: "",
        affiliationType: "Founder",
        description: "",
        content: "",
        status: "ACTIVE"
    });

    const breadcrumbs = [
        { title: "Dashboard", url: "/admin/dashboard" },
        { title: "CMS", url: "" },
        { title: "Agreement Management", url: "/admin/cms/agreements/list/1" },
        { title: "Create Template", url: "" },
    ];

    const affiliationOptions = [
        { label: "Founder Agreement (Default)", value: "Founder" },
        { label: "Standard Commercial Agreement", value: "Standard" },
        { label: "Partner Agreement", value: "Partner" }
    ];

    const statusOptions = [
        { label: "ACTIVE (Sets as current template for this affiliation type)", value: "ACTIVE" },
        { label: "INACTIVE", value: "INACTIVE" }
    ];

    useEffect(() => {
        const fetchPlaceholders = async () => {
            try {
                const res = await agreementService.getPlaceholders();
                if (res && (res.success || Array.isArray(res.data))) {
                    setPlaceholders(res.data || []);
                }
            } catch (err) {
                console.error("Failed to fetch placeholders:", err);
            }
        };
        fetchPlaceholders();
    }, []);

    // Insert dynamic placeholder chip at cursor position in CKEditor
    const handleInsertPlaceholder = (placeholderKey) => {
        const editorId = "agreementContentEditor";
        if (window.CKEDITOR && window.CKEDITOR.instances[editorId]) {
            const editor = window.CKEDITOR.instances[editorId];
            editor.insertText(placeholderKey);
            const data = editor.getData();
            setFormData((prev) => ({ ...prev, content: data }));
        } else {
            setFormData((prev) => ({ ...prev, content: (prev.content || "") + " " + placeholderKey }));
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        const editorId = "agreementContentEditor";
        let contentToSubmit = formData.content;
        if (window.CKEDITOR && window.CKEDITOR.instances[editorId]) {
            contentToSubmit = window.CKEDITOR.instances[editorId].getData();
        }

        if (!formData.title.trim()) {
            Swal.fire("Validation Error", "Template title is required.", "error");
            return;
        }
        if (!contentToSubmit || !contentToSubmit.trim()) {
            Swal.fire("Validation Error", "Agreement template content is required.", "error");
            return;
        }

        setLoading(true);
        try {
            const res = await agreementService.createTemplate({ ...formData, content: contentToSubmit });
            if (res && res.success) {
                Swal.fire("Success", "Agreement template created successfully!", "success");
                navigate("/admin/cms/agreements/list/1");
            } else {
                Swal.fire("Error", res?.message || "Failed to create agreement template.", "error");
            }
        } catch (err) {
            Swal.fire("Error", err?.message || "An error occurred.", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="main-content header-dark">
            <div className="container-fluid">
                <Breadcrums breadcrumbs={breadcrumbs} />

                <div className="row row-sm">
                    <div className="col-lg-12">
                        <div className="card custom-card">
                            <div className="card-header border-bottom-0 d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="card-title font-weight-bold mb-1">Create Commercial Agreement Template</h4>
                                    <p className="tx-12 tx-gray-500 mb-0">
                                        Define agreement terms and insert dynamic club database placeholders.
                                    </p>
                                </div>
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate("/admin/cms/agreements/list/1")}
                                >
                                    <i className="fa fa-arrow-left me-1"></i> Back to List
                                </button>
                            </div>

                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label font-weight-bold">
                                                Template Title <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="e.g. Founder Commercial Services Agreement"
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-3 mb-3">
                                            <label className="form-label font-weight-bold">Club Affiliation Type</label>
                                            <select
                                                className="form-select"
                                                value={formData.affiliationType}
                                                onChange={(e) => setFormData({ ...formData, affiliationType: e.target.value })}
                                            >
                                                {affiliationOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-3 mb-3">
                                            <label className="form-label font-weight-bold">Status</label>
                                            <select
                                                className="form-select"
                                                value={formData.status}
                                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            >
                                                {statusOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label font-weight-bold">Description / Internal Notes</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Optional brief description of this agreement version"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>

                                    {/* Dynamic Placeholders Toolbar */}
                                    <div className="card bg-light border p-3 mb-3">
                                        <div className="d-flex align-items-center mb-2">
                                            <i className="fa fa-code text-primary me-2 tx-18"></i>
                                            <h6 className="mb-0 font-weight-bold text-dark">Available Club Details Dynamic Placeholders</h6>
                                        </div>
                                        <p className="tx-12 text-muted mb-2">
                                            Click any tag below to insert dynamic club data from the database directly into the agreement template.
                                        </p>

                                        <div className="d-flex flex-wrap gap-2">
                                            {placeholders.map((p) => (
                                                <Whisper
                                                    key={p.key}
                                                    placement="top"
                                                    trigger="hover"
                                                    speaker={
                                                        <Tooltip>
                                                            <strong>{p.label}</strong> (Sample: <em>{p.sample}</em>)
                                                        </Tooltip>
                                                    }
                                                >
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-info bg-white text-dark me-2 mb-2"
                                                        style={{ borderRadius: "20px", fontWeight: "600", borderColor: "#007d7f" }}
                                                        onClick={() => handleInsertPlaceholder(p.key)}
                                                    >
                                                        <span style={{ color: "#007d7f", marginRight: "4px" }}>+</span> {p.key}
                                                    </button>
                                                </Whisper>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Agreement Content Editor */}
                                    <div className="mb-4">
                                        <label className="form-label font-weight-bold">
                                            Agreement Template Terms & Conditions <span className="text-danger">*</span>
                                        </label>
                                        <CustomCkeditor
                                            id="agreementContentEditor"
                                            fieldname="content"
                                            data={formData.content}
                                            value={formData.content}
                                            onChange={(event, editor) => {
                                                const data = editor.getData();
                                                setFormData((prev) => ({ ...prev, content: data }));
                                            }}
                                            setFieldValue={(name, value) => {
                                                setFormData((prev) => ({ ...prev, content: value }));
                                            }}
                                        />
                                    </div>

                                    <div className="d-flex justify-content-end gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-secondary me-2"
                                            onClick={() => navigate("/admin/cms/agreements/list/1")}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary text-white"
                                            disabled={loading}
                                            style={{ backgroundColor: "#007d7f", borderColor: "#007d7f" }}
                                        >
                                            {loading ? "Saving Template..." : "Save Agreement Template"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddAgreementTemplate;
